/**
 * Confirm Context - Global confirmation dialog system
 * 
 * Usage:
 *   import { useConfirm } from '../Context/ConfirmContext';
 *   
 *   const { confirm } = useConfirm();
 *   
 *   // Returns a promise that resolves to true/false
 *   const result = await confirm({
 *     title: 'Delete Item',
 *     message: 'Are you sure you want to delete this item?',
 *     confirmText: 'Delete',
 *     cancelText: 'Cancel',
 *     type: 'danger' // 'danger', 'warning', 'info'
 *   });
 *   
 *   if (result) {
 *     // User clicked confirm
 *   }
 */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';

// ============================================
// ANIMATIONS
// ============================================

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

// ============================================
// STYLED COMPONENTS
// ============================================

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999999;
  animation: ${fadeIn} 0.2s ease;
  padding: 20px;
`;

const Dialog = styled.div`
  background: linear-gradient(180deg, #1e293b 0%, #0f172a 100%);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  max-width: 420px;
  width: 100%;
  overflow: hidden;
  animation: ${scaleIn} 0.25s ease;
`;

const DialogHeader = styled.div`
  padding: 24px 24px 0 24px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
  background: ${props => {
    switch (props.$type) {
      case 'danger': return 'rgba(239, 68, 68, 0.15)';
      case 'warning': return 'rgba(245, 158, 11, 0.15)';
      default: return 'rgba(99, 102, 241, 0.15)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.$type) {
      case 'danger': return 'rgba(239, 68, 68, 0.3)';
      case 'warning': return 'rgba(245, 158, 11, 0.3)';
      default: return 'rgba(99, 102, 241, 0.3)';
    }
  }};
`;

const TitleWrapper = styled.div`
  flex: 1;
`;

const Title = styled.h3`
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
`;

const Message = styled.p`
  margin: 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
`;

const DialogBody = styled.div`
  padding: 20px 24px;
`;

const DialogActions = styled.div`
  padding: 16px 24px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  ${props => props.$primary ? `
    background: ${props.$type === 'danger' 
      ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' 
      : props.$type === 'warning'
      ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
      : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'};
    color: white;
    box-shadow: 0 4px 12px ${props.$type === 'danger' 
      ? 'rgba(239, 68, 68, 0.3)' 
      : props.$type === 'warning'
      ? 'rgba(245, 158, 11, 0.3)'
      : 'rgba(99, 102, 241, 0.3)'};
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px ${props.$type === 'danger' 
        ? 'rgba(239, 68, 68, 0.4)' 
        : props.$type === 'warning'
        ? 'rgba(245, 158, 11, 0.4)'
        : 'rgba(99, 102, 241, 0.4)'};
    }
  ` : `
    background: rgba(30, 41, 59, 0.8);
    color: rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(99, 102, 241, 0.3);
    
    &:hover {
      background: rgba(30, 41, 59, 1);
      color: white;
    }
  `}
  
  &:active {
    transform: translateY(0);
  }
`;

// ============================================
// CONTEXT
// ============================================

const ConfirmContext = createContext();

export const ConfirmProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);
  const [resolvePromise, setResolvePromise] = useState(null);

  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      setResolvePromise(() => resolve);
      setDialog({
        title: options.title || 'Confirm',
        message: options.message || 'Are you sure?',
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        type: options.type || 'info', // 'danger', 'warning', 'info'
        icon: options.icon
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (resolvePromise) {
      resolvePromise(true);
    }
    setDialog(null);
    setResolvePromise(null);
  }, [resolvePromise]);

  const handleCancel = useCallback(() => {
    if (resolvePromise) {
      resolvePromise(false);
    }
    setDialog(null);
    setResolvePromise(null);
  }, [resolvePromise]);

  const getIcon = (type) => {
    switch (type) {
      case 'danger': return '🗑️';
      case 'warning': return '⚠️';
      default: return 'ℹ️';
    }
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}
      {dialog && createPortal(
        <Overlay onClick={handleCancel}>
          <Dialog onClick={e => e.stopPropagation()}>
            <DialogHeader>
              <IconWrapper $type={dialog.type}>
                {dialog.icon || getIcon(dialog.type)}
              </IconWrapper>
              <TitleWrapper>
                <Title>{dialog.title}</Title>
                <Message>{dialog.message}</Message>
              </TitleWrapper>
            </DialogHeader>
            <DialogActions>
              <Button onClick={handleCancel}>
                {dialog.cancelText}
              </Button>
              <Button $primary $type={dialog.type} onClick={handleConfirm}>
                {dialog.confirmText}
              </Button>
            </DialogActions>
          </Dialog>
        </Overlay>,
        document.body
      )}
    </ConfirmContext.Provider>
  );
};

// ============================================
// HOOK
// ============================================

export const useConfirm = () => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
};

export default ConfirmContext;
